import React from "react";
import { Helmet } from "react-helmet";
import Hero from "./Hero";
import Footer from "../../footer/Footer";
import Section1 from "./Section1";
import FAQ from "./FAQ";
import SocialProof from "../../utils/SocialProof";
import ContactSalesCTA from "../../utils/ContactSalesCTA";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { getSolutionsByCategory } from "../Solutions/solutions.config";

const homeStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Zenstream",
  url: "https://zenstream.app/",
  applicationCategory: "VideoEditingApplication",
  operatingSystem: "Web, Windows, macOS",
  description:
    "Zenstream is an AI content intelligence platform with real-time livestream censoring, multimodal moderation, and a static image API — all driven by natural-language triggers.",
  offers: {
    "@type": "AggregateOffer",
    lowPrice: "0",
    highPrice: "79",
    priceCurrency: "USD",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "1024",
  },
};

function SolutionsTeaser() {
  const grouped = getSolutionsByCategory();
  return (
    <section
      aria-label="Solutions overview"
      className="w-full bg-white py-16 sm:py-24"
    >
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-12">
          <span className="inline-block text-xs sm:text-sm uppercase tracking-[0.25em] text-black font-semibold mb-3">
            Solutions
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-black font-display">
            Built for every team that ships content
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4 text-base sm:text-lg leading-relaxed">
            Same engine, same English-language triggers, tuned to your industry.
          </p>
        </div>
        <div className="grid lg:grid-cols-3 gap-8">
          {grouped.map(({ category, items }) => (
            <div
              key={category}
              className="rounded-3xl border border-gray-200/70 bg-white p-6 shadow-sm"
            >
              <div className="text-sm font-semibold text-zinc-900 border-b border-gray-200 pb-3 mb-3">
                {category}
              </div>
              <ul className="space-y-2">
                {items.map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/solutions/${s.slug}`}
                      className="text-neutral-700 hover:text-black transition"
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/solutions"
            className="inline-flex items-center justify-center bg-black text-white font-semibold px-7 py-3 rounded-full hover:bg-neutral-800 transition"
          >
            See every solution →
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>
          Zenstream | Natural-Language Content Intelligence & Livestream
          Censoring
        </title>
        <meta
          name="description"
          content="Zenstream detects and removes specific images, songs, sounds, and scenes with natural-language triggers. Real-time livestream censoring with OBS, multimodal moderation, and a static image API. Trusted by 1,000+ creators and platforms."
        />
        <link rel="canonical" href="https://zenstream.app/" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">
          {JSON.stringify(homeStructuredData)}
        </script>
      </Helmet>

      <main className="flex flex-col w-full min-h-screen pb-28 sm:pb-24">
        <Hero />
        <SocialProof />
        <Section1 />
        <SolutionsTeaser />
        <ContactSalesCTA topic="sales" />
        <FAQ />
        <Footer />

        {!currentUser && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-black border-t border-white/10 pb-safe">
            <div className="max-w-6xl mx-auto px-4 py-3 md:py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className="text-white font-medium text-center sm:text-left text-sm sm:text-base">
                Free version — 1 free video every month.
              </div>
              <button
                onClick={() => navigate("/login")}
                className="px-6 py-2 bg-white text-black font-semibold rounded-full hover:bg-neutral-200 transition"
              >
                Sign up
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
